import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserPlus, Phone, Mail, MapPin, Clock, AlertCircle, CheckCircle, XCircle, Plus, Filter, PhoneOff, Calendar, Wrench, Home, Target, Users, ChevronDown, FileText } from 'lucide-react';
import { clients, getClientDisplayName, getClientById } from '../data/clientsData';
import ClientSearchBar from './ClientSearchBar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ProspectsEntrants = () => {
  const navigate = useNavigate();
  const [prospects, setProspects] = useState([]); // Démarrer avec un tableau vide
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false); // Pour différencier l'affichage admin/commercial
  const [activeTab, setActiveTab] = useState('opportunite');
  const [activeSubTab, setActiveSubTab] = useState('nouveau-prospect');
  const [activeSubTabPerdu, setActiveSubTabPerdu] = useState('faux-numero');
  const [selectedStatusProspect, setSelectedStatusProspect] = useState(null);
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [statusCommentaire, setStatusCommentaire] = useState(''); // Commentaire lors du changement de statut
  const dropdownRef = useRef(null);
  const [activeNRPTab, setActiveNRPTab] = useState('ne-repond-pas');
  
  // États pour le modal de note rapide
  const [noteModalOpen, setNoteModalOpen] = useState(false);
  const [selectedNoteProspect, setSelectedNoteProspect] = useState(null);
  const [noteText, setNoteText] = useState('');
  
  // États pour le modal de parrainage
  const [parrainageModalOpen, setParrainageModalOpen] = useState(false);
  const [selectedClientParrain, setSelectedClientParrain] = useState(null);
  const [newProspectData, setNewProspectData] = useState({
    prenom: '',
    nom: '',
    numero_telephone: '',
    mail: '',
    adresse: '',
    ville: '',
    code_postal: '',
    statut: 'Nouveau prospect',
    category: 'opportunite',
    notes: ''
  });

  // Charger les prospects depuis l'API au montage du composant
  useEffect(() => {
    const loadProspects = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        
        // Définir si c'est un admin
        setIsAdmin(user.role === 'admin');
        
        // Les admins utilisent un endpoint différent pour voir tous les prospects
        const endpoint = user.role === 'admin' 
          ? `${BACKEND_URL}/api/admin/prospects-entrants`
          : `${BACKEND_URL}/api/mes-prospects`;
        
        const response = await fetch(endpoint, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error('Erreur lors du chargement des prospects');
        }

        const data = await response.json();
        
        // DEBUG: Afficher les premiers prospects bruts de l'API
        console.log('[DEBUG] Premier prospect brut de l\'API:', data[0]);
        
        // Mapper les données de l'API vers le format attendu par le frontend
const formattedProspects = data.map(prospect => {
  // Normaliser le statut
  let statut = prospect.status || 'Nouveau prospect';

  const normalizeStatut = (value) => {
    if (!value) return 'Nouveau prospect';

    const upper = value.toUpperCase();

    if (upper === 'EN_ATTENTE') return 'Nouveau prospect';
    if (upper === 'NOUVEAU PROSPECT') return 'Nouveau prospect';
    if (upper === 'À RAPPELER') return 'À rappeler';
    if (upper === 'DEVIS ENVOYÉ') return 'Devis envoyé';
    if (upper === 'DEVIS SIGNÉ') return 'Devis signé';
    if (upper === 'INSTALLATION PROGRAMMÉE') return 'Installation programmée';
    if (upper === 'INSTALLATION CONFIRMÉE') return 'Installation confirmée';
    if (upper === 'CLIENT INSTALLÉ') return 'Client installé';
    if (upper === 'FAUX NUMÉRO') return 'Faux numéro';
    if (upper === 'HORS CIBLE') return 'Hors cible';
    if (upper === 'PAS INTÉRESSÉ') return 'Pas intéressé';
    if (upper === 'ANNULÉ') return 'Annulé';
    if (upper === 'NE RÉPOND PAS') return 'Ne répond pas';
    if (upper === 'NE RÉPOND PAS 2') return 'Ne répond pas 2';
    if (upper === 'NE RÉPOND PAS 3') return 'Ne répond pas 3';
    if (upper === 'NE RÉPOND PAS 3+') return 'Ne répond pas 3+';

    return value;
  };

  statut = normalizeStatut(statut);
          
          // 🔒 Déterminer la catégorie automatiquement basée sur le statut
          const statusPerdu = ['Faux numéro', 'Mauvais numéro', 'Hors cible', 'Pas intéressé', 'Annulé', 'Injoignable', 'Rejeté'];
          const category = statusPerdu.includes(statut) ? 'perdu' : 'opportunite';
          
          return {
            id: prospect.id,
            prenom: prospect.prenom,
            nom: prospect.nom,
            email: prospect.email || '',
            telephone: prospect.telephone,
            adresse: prospect.adresse || '',
            ville: prospect.ville || '',
            code_postal: prospect.code_postal || '',
            statut: statut,
            category: category, // Catégorie automatique basée sur le statut
            dateArrivee: prospect.date_assignation || prospect.date_reception || prospect.created_at,
            source: prospect.source || 'Web',
            assignePar: 'Auto',
            priorite: 'Normale',
            notes: prospect.notes || '',
            // Informations pour l'admin
            commercial_assigne_id: prospect.commercial_assigne_id || null,
            commercial_nom: prospect.commercial_nom || null,
            historique: prospect.historique || [],
            // Tracking "Ne répond pas"
            niveau_ne_repond_pas: prospect.niveau_ne_repond_pas !== undefined ? prospect.niveau_ne_repond_pas : 0,
            // Date d'assignation
            date_assignation: prospect.date_assignation || prospect.created_at
          };
        });

        // DEBUG: Afficher le premier prospect formaté
        console.log('[DEBUG] Premier prospect formaté:', formattedProspects[0]);
        
        // Trier les prospects du plus ancien au plus récent (par date d'assignation)
        const sortedProspects = formattedProspects.sort((a, b) => {
          const dateA = new Date(a.date_assignation || a.dateArrivee);
          const dateB = new Date(b.date_assignation || b.dateArrivee);
          return dateA - dateB; // Tri ascendant (plus ancien en premier)
        });
        
        setProspects(sortedProspects);
        console.log(`✅ ${sortedProspects.length} prospects chargés depuis l'API (triés du plus ancien au plus récent)`);
      } catch (error) {
        console.error('❌ Erreur lors du chargement des prospects:', error);
        // Garder le tableau vide en cas d'erreur
        setProspects([]);
      } finally {
        setLoading(false);
      }
    };

    loadProspects();
    
    // 🔄 AUTO-REFRESH : Recharger les prospects toutes les 30 secondes
    const refreshInterval = setInterval(() => {
      loadProspects();
    }, 30000); // 30 secondes

    // Nettoyer l'intervalle au démontage
    return () => clearInterval(refreshInterval);
  }, []); // Exécuter une seule fois au montage

  // Fonction pour naviguer vers la fiche de rendez-vous
  const handleProspectClick = (e, prospect) => {
    e.preventDefault();
    e.stopPropagation();
    navigate(`/prospects-entrants/${prospect.id}/fiche-rdv`);
  };

  // Fonction pour gérer le clic sur le statut
  const handleStatusClick = (e, prospect) => {
    e.preventDefault();
    e.stopPropagation();
    console.log('🔥 Status click called for:', prospect.prenom, prospect.nom);
    console.log('🔥 Setting selectedStatusProspect:', prospect);
    setSelectedStatusProspect(prospect);
    setStatusDropdownOpen(true);
    console.log('🔥 Modal should open now');
  };

  // Fonction pour ouvrir le modal de parrainage
  const handleParrainageClick = () => {
    setParrainageModalOpen(true);
  };

  // Fonction pour sauvegarder le nouveau prospect parrainé
  const handleSaveNewProspect = () => {
    if (!newProspectData.prenom || !newProspectData.nom || !newProspectData.numero_telephone) {
      console.log('Veuillez remplir au minimum le prénom, nom et numéro de téléphone');
      return;
    }

    // Récupérer les informations du client parrain depuis la barre de recherche
    const parrainageInfo = selectedClientParrain 
      ? `Parrainage: ${getClientDisplayName(selectedClientParrain)}`
      : 'Parrainage Commercial';

    const newProspect = {
      id: Math.max(...prospects.map(p => p.id)) + 1, // Générer un nouvel ID
      ...newProspectData,
      dateArrivee: new Date().toISOString(),
      assignePar: parrainageInfo,
      source: 'Parrainage',
      priorite: 'Normale',
      email: newProspectData.mail,
      telephone: newProspectData.numero_telephone,
      dernierContact: new Date().toISOString(),
      clientParrainId: selectedClientParrain?.id || null,
      clientParrainNom: selectedClientParrain ? getClientDisplayName(selectedClientParrain) : null
    };

    // Ajouter le nouveau prospect à la liste
    setProspects(prevProspects => [...prevProspects, newProspect]);

    // Fermer le modal et réinitialiser le formulaire
    setParrainageModalOpen(false);
    setSelectedClientParrain(null);
    setNewProspectData({
      prenom: '',
      nom: '',
      numero_telephone: '',
      mail: '',
      adresse: '',
      ville: '',
      code_postal: '',
      statut: 'Nouveau prospect',
      category: 'opportunite',
      notes: ''
    });

    // Changer vers l'onglet approprié si nécessaire
    const newSubTabId = getSubTabId(newProspectData.statut);
    if (newProspectData.category === 'opportunite' && newSubTabId !== activeSubTab) {
      setActiveSubTab(newSubTabId);
    } else if (newProspectData.category === 'perdu' && activeTab !== 'perdu') {
      setActiveTab('perdu');
      const perduSubTabId = getSubTabIdPerdu(newProspectData.statut);
      if (perduSubTabId) {
        setActiveSubTabPerdu(perduSubTabId);
      }
    }

    // Plus d'alerte - le prospect apparaît directement dans la liste
  };

  // Fonction pour changer le statut d'un prospect
 const handleStatusChange = (newStatus, newCategory) => {
  if (!selectedStatusProspect) return;

  const prospectId = selectedStatusProspect.id;
  const oldProspects = prospects;
  const oldSelectedStatusProspect = selectedStatusProspect;
  const commentaireToSend = statusCommentaire || null;

  const optimisticProspectData = {
    ...selectedStatusProspect,
    statut: newStatus,
    category: newCategory,
    dateModification: new Date().toISOString()
  };

  // 1) changement immédiat dans le CRM
  setProspects(prevProspects =>
    prevProspects.map(prospect =>
      prospect.id === prospectId ? optimisticProspectData : prospect
    )
  );

  // 2) on ferme la popup immédiatement
  setStatusDropdownOpen(false);
  setSelectedStatusProspect(null);
  setStatusCommentaire('');

  // 3) appel backend en arrière-plan (sans bloquer)
  (async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/prospects/mes-prospects/${prospectId}/status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          status: newStatus,
          commentaire: commentaireToSend
        })
      });

      if (!response.ok) {
        throw new Error();
      }

      console.log('✅ statut envoyé au backend');
    } catch (error) {
      console.error('❌ erreur update statut');

      // rollback si erreur
      setProspects(oldProspects);
      setSelectedStatusProspect(oldSelectedStatusProspect);
    }
  })();
};

  // Fonction pour ouvrir le modal de note
  const handleNoteClick = (e, prospect) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedNoteProspect(prospect);
    setNoteText('');
    setNoteModalOpen(true);
  };

  // Fonction pour sauvegarder une note rapide
  const handleSaveNote = async () => {
    if (!noteText.trim()) {
      console.log('Veuillez entrer une note');
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/mes-prospects/${selectedNoteProspect.id}/notes`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ commentaire: noteText })
      });

      if (!response.ok) {
        throw new Error('Erreur lors de l\'ajout de la note');
      }

      // Recharger le prospect pour avoir l'historique à jour
      const prospectResponse = await fetch(`${BACKEND_URL}/api/mes-prospects/${selectedNoteProspect.id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (prospectResponse.ok) {
        const updatedProspect = await prospectResponse.json();
        setProspects(prevProspects => 
          prevProspects.map(p => 
            p.id === selectedNoteProspect.id ? { ...p, historique: updatedProspect.historique } : p
          )
        );
      }

      console.log('✅ Note ajoutée avec succès');
      setNoteModalOpen(false);
      setNoteText('');
      setSelectedNoteProspect(null);
    } catch (error) {
      console.error('❌ Erreur lors de l\'ajout de la note:', error);
      console.log('Erreur lors de l\'ajout de la note. Veuillez réessayer.');
    }
  };

  // Fermer le dropdown si on clique à l'extérieur
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setStatusDropdownOpen(false);
        setSelectedStatusProspect(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Définition des sous-onglets d'opportunité
  const sousOngletsOpportunite = [
    { id: 'nouveau-prospect', label: 'Nouveau prospect', icon: UserPlus },
    { id: 'ne-repond-pas', label: 'Ne répond pas', icon: PhoneOff },
    { id: 'a-rappeler', label: 'À rappeler', icon: Calendar },
    { id: 'devis-envoye', label: 'Devis envoyé', icon: FileText },
    { id: 'devis-signe', label: 'Devis signé', icon: CheckCircle },
    { id: 'installation-programmee', label: 'Installation programmée', icon: Clock },
    { id: 'installation-confirmee', label: 'Installation confirmée', icon: Wrench },
    { id: 'client-installe', label: 'Client installé', icon: Home }
  ];

  // Définition des sous-onglets pour Perdu
  const sousOngletsPerdu = [
    { id: 'faux-numero', label: 'Faux numéro / Mauvais numéro', icon: PhoneOff },
    { id: 'hors-cible', label: 'Hors cible', icon: Target },
    { id: 'pas-interesse', label: 'Pas intéressé', icon: XCircle },
    { id: 'annule', label: 'Annulé', icon: AlertCircle }
  ];

  // Tous les statuts disponibles pour le changement
  const allStatuses = [
    { category: 'opportunite', label: 'Nouveau prospect', icon: UserPlus, color: 'bg-blue-100 text-blue-800' },
    { category: 'opportunite', label: 'Ne répond pas', icon: PhoneOff, color: 'bg-gray-100 text-gray-800' },
    { category: 'opportunite', label: 'Ne répond pas 2', icon: PhoneOff, color: 'bg-gray-100 text-gray-800' },
    { category: 'opportunite', label: 'Ne répond pas 3', icon: PhoneOff, color: 'bg-gray-100 text-gray-800' },
    { category: 'opportunite', label: 'Ne répond pas 3+', icon: PhoneOff, color: 'bg-gray-100 text-gray-800' },
    { category: 'opportunite', label: 'À rappeler', icon: Calendar, color: 'bg-yellow-100 text-yellow-800' },
    { category: 'opportunite', label: 'Devis envoyé', icon: FileText, color: 'bg-purple-100 text-purple-800' },
    { category: 'opportunite', label: 'Devis signé', icon: CheckCircle, color: 'bg-green-100 text-green-800' },
    { category: 'opportunite', label: 'Installation programmée', icon: Clock, color: 'bg-green-100 text-green-800' },
    { category: 'opportunite', label: 'Installation confirmée', icon: Wrench, color: 'bg-green-100 text-green-800' },
    { category: 'opportunite', label: 'Client installé', icon: Home, color: 'bg-green-100 text-green-800' },
    { category: 'perdu', label: 'Faux numéro', icon: PhoneOff, color: 'bg-red-100 text-red-800' },
    { category: 'perdu', label: 'Hors cible', icon: Target, color: 'bg-red-100 text-red-800' },
    { category: 'perdu', label: 'Pas intéressé', icon: XCircle, color: 'bg-red-100 text-red-800' },
    { category: 'perdu', label: 'Annulé', icon: AlertCircle, color: 'bg-red-100 text-red-800' }
  ];

  // Fonction pour vérifier si un statut est disponible pour un prospect
  const isStatusAvailable = (status, prospect) => {
    // LOGIQUE DE VERROUILLAGE PROGRESSIF "NE RÉPOND PAS"
    const neRepondPasStatuts = {
      'Ne répond pas': 1,
      'Ne répond pas 2': 2,
      'Ne répond pas 3': 3,
      'Ne répond pas 3+': 4
    };
    
    // Si le statut est un des "Ne répond pas"
    if (status.label in neRepondPasStatuts) {
      const niveauDemande = neRepondPasStatuts[status.label];
      const niveauActuel = prospect.niveau_ne_repond_pas !== undefined ? prospect.niveau_ne_repond_pas : 0;
      
      console.log(`[DEBUG] Vérification "${status.label}" pour ${prospect.prenom} ${prospect.nom}:`, {
        niveauDemande,
        niveauActuel,
        disponible: niveauDemande === niveauActuel + 1
      });
      
      // Le statut est disponible seulement si c'est le niveau suivant
      // Niveau 0 → peut sélectionner niveau 1 ("Ne répond pas")
      // Niveau 1 → peut sélectionner niveau 2 ("Ne répond pas 2")
      // Niveau 2 → peut sélectionner niveau 3 ("Ne répond pas 3")
      // Niveau 3 → peut sélectionner niveau 4 ("Ne répond pas 3+")
      return niveauDemande === niveauActuel + 1;
    }
    
    // Tous les autres statuts sont toujours disponibles
    return true;
  };

  // Fonction pour convertir le statut en ID de sous-onglet (Opportunité)
  const getSubTabId = (statut) => {
    const mapping = {
      'Nouveau prospect': 'nouveau-prospect',
      'Ne répond pas': 'ne-repond-pas',
      'Ne répond pas 2': 'ne-repond-pas',
      'Ne répond pas 3': 'ne-repond-pas',
      'Ne répond pas 3+': 'ne-repond-pas',
      'À rappeler': 'a-rappeler',
      'Devis envoyé': 'devis-envoye',
      'Devis signé': 'devis-signe',
      'Installation programmée': 'installation-programmee',
      'Installation confirmée': 'installation-confirmee',
      'Client installé': 'client-installe'
    };
    // 🔒 CORRECTION : Ne PAS retourner 'nouveau-prospect' par défaut
    // Si le statut n'est pas dans le mapping, retourner null pour que le lead soit exclu
    return mapping[statut] || null;
  };

  // Fonction pour convertir le statut en ID de sous-onglet (Perdu)
  const getSubTabIdPerdu = (statut) => {
    const mapping = {
      'Faux numéro': 'faux-numero',
      'Mauvais numéro': 'faux-numero',
      'Hors cible': 'hors-cible',
      'Pas intéressé': 'pas-interesse',
      'Annulé': 'annule',
      'Injoignable': 'faux-numero' // On peut regrouper avec faux numéro
    };
    return mapping[statut] || 'faux-numero';
  };

  // Filtrer les prospects selon l'onglet et sous-onglet actifs
const nrpStatutMap = {
  'ne-repond-pas': 'Ne répond pas',
  'ne-repond-pas-2': 'Ne répond pas 2',
  'ne-repond-pas-3': 'Ne répond pas 3',
  'ne-repond-pas-3plus': 'Ne répond pas 3+',
};
const filteredProspects = activeTab === 'opportunite' 
  ? prospects.filter(prospect => {
      if (!prospect.category === 'opportunite') return false;
      if (prospect.category !== 'opportunite') return false;
      if (activeSubTab === 'ne-repond-pas') {
        return prospect.statut === nrpStatutMap[activeNRPTab];
      }
      return getSubTabId(prospect.statut) === activeSubTab;
    })
  : prospects.filter(prospect => 
      prospect.category === 'perdu' && 
      getSubTabIdPerdu(prospect.statut) === activeSubTabPerdu
    );

  // Onglet "Client installé" : afficher du plus récent au plus ancien
  if (activeSubTab === 'client-installe') {
    filteredProspects.sort((a, b) => {
      const dateA = new Date(a.date_assignation || a.dateArrivee);
      const dateB = new Date(b.date_assignation || b.dateArrivee);
      return dateB - dateA; // Tri descendant (plus récent en premier)
    });
  }
  // Debug: Log des premiers prospects pour vérifier la structure
  console.log('Debug prospects filtrés:', filteredProspects.slice(0, 2).map(p => ({
    id: p.id,
    nom: p.nom,
    prenom: p.prenom,
    statut: p.statut
  })));

  // Compter les prospects par catégorie et sous-catégorie
  const opportuniteCount = prospects.filter(p => p.category === 'opportunite').length;
  const perduCount = prospects.filter(p => p.category === 'perdu').length;
  
  // Fonction pour obtenir la date d'aujourd'hui au format YYYY-MM-DD
  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0]; // Format YYYY-MM-DD
  };
  
  // Compter uniquement les prospects assignés aujourd'hui
  const getTodayProspectsCount = () => {
    const today = getTodayDate();
    return prospects.filter(prospect => {
      // Convertir dateArrivee en format YYYY-MM-DD pour comparaison
      const prospectDate = new Date(prospect.dateArrivee).toISOString().split('T')[0];
      return prospectDate === today;
    }).length;
  };
  
  const todayProspectsCount = getTodayProspectsCount();


  
  // Compter les prospects par sous-onglet d'opportunité
  const getSubTabCount = (subTabId) => {
    return prospects.filter(p => 
      p.category === 'opportunite' && 
      getSubTabId(p.statut) === subTabId
    ).length;
  };

  // Compter les prospects par sous-onglet de Perdu
  const getSubTabCountPerdu = (subTabId) => {
    return prospects.filter(p => 
      p.category === 'perdu' && 
      getSubTabIdPerdu(p.statut) === subTabId
    ).length;
  };

  // Fonction pour obtenir la couleur du statut
  const getStatutColor = (statut) => {
    // IMPORTANT: Tester "Ne répond pas" EN PREMIER avant "rappeler" pour éviter les conflits
    if (statut.includes('Ne répond pas')) return 'bg-gray-100 text-gray-800';
    if (statut.includes('Nouveau')) return 'bg-blue-100 text-blue-800';
    if (statut.includes('rappeler')) return 'bg-yellow-100 text-yellow-800';
    if (statut.includes('Devis envoyé')) return 'bg-purple-100 text-purple-800';
    if (statut.includes('Devis signé')) return 'bg-green-100 text-green-800';
    if (statut.includes('Installation')) return 'bg-green-100 text-green-800';
    if (statut.includes('Client installé')) return 'bg-green-100 text-green-800';
    if (statut.includes('Faux') || statut.includes('Pas intéressé') || statut.includes('Hors cible')) return 'bg-red-100 text-red-800';
    return 'bg-gray-100 text-gray-800';
  };

  // Fonction pour obtenir l'icône du statut
  const getStatutIcon = (statut) => {
    if (statut.includes('Nouveau')) return <UserPlus className="w-4 h-4" />;
    if (statut.includes('rappeler')) return <Calendar className="w-4 h-4" />;
    if (statut.includes('Devis')) return <FileText className="w-4 h-4" />;
    if (statut.includes('Installation')) return <Wrench className="w-4 h-4" />;
    if (statut.includes('Client installé')) return <Home className="w-4 h-4" />;
    if (statut.includes('Faux') || statut.includes('Pas intéressé') || statut.includes('Hors cible')) return <XCircle className="w-4 h-4" />;
    if (statut.includes('Ne répond pas')) return <PhoneOff className="w-4 h-4" />;
    return <AlertCircle className="w-4 h-4" />;
  };

  // Fonction pour formater le numéro de téléphone français : +33612345678 → 06 12 34 56 78
  const formatPhoneNumber = (phone) => {
    if (!phone) return '';
    
    // Retirer tous les espaces, tirets, points, parenthèses existants
    let cleaned = phone.replace(/[\s\-\.\(\)]/g, '');
    
    // Si le numéro commence par +33, remplacer par 0
    if (cleaned.startsWith('+33')) {
      cleaned = '0' + cleaned.substring(3);
    }
    
    // Si le numéro commence par 0033, remplacer par 0
    if (cleaned.startsWith('0033')) {
      cleaned = '0' + cleaned.substring(4);
    }
    
    // Enlever tous les caractères non numériques restants
    cleaned = cleaned.replace(/\D/g, '');
    
    // S'assurer qu'il commence par 0 si ce n'est pas le cas et qu'il a 9 chiffres
    if (cleaned.length === 9 && !cleaned.startsWith('0')) {
      cleaned = '0' + cleaned;
    }
    
    // Ajouter un espace tous les 2 chiffres
    // Ex: 0612345678 → 06 12 34 56 78
    if (cleaned.length === 10) {
      return cleaned.match(/.{1,2}/g).join(' ');
    }
    
    // Si le format est différent, retourner tel quel
    return phone;
  };

  // Fonction pour vérifier si un prospect a des notes récentes (dernières 24h)
  const hasRecentNotes = (prospect) => {
    if (!prospect.historique || prospect.historique.length === 0) return false;
    
    const oneDayAgo = new Date();
    oneDayAgo.setHours(oneDayAgo.getHours() - 24);
    
    return prospect.historique.some(entry => {
      if (entry.type !== 'note') return false;
      const entryDate = new Date(entry.date);
      return entryDate > oneDayAgo;
    });
  };

  // Fonction pour obtenir la couleur du statut
  const getStatusColor = (statut) => {
    switch(statut) {
      case 'Nouveau prospect':
        return '#C5DFF4'; // RGB(197, 223, 244) - Bleu personnalisé
      case 'Ne répond pas':
        return '#FFE59F'; // RGB(255, 229, 159) - Jaune
      case 'À rappeler':
        return '#FDE68A'; // RGB(253, 230, 138) - Jaune doré
      case 'Devis envoyé':
        return '#E9D5FF'; // RGB(233, 213, 255) - Violet clair
      case 'Devis signé':
        return '#D1FAE5'; // RGB(209, 250, 229) - Vert clair
      case 'Installation programmée':
        return '#A7F3D0'; // RGB(167, 243, 208) - Vert menthe
      case 'Installation confirmée':
        return '#6EE7B7'; // RGB(110, 231, 183) - Vert moyen
      case 'Client installé':
        return '#34D399'; // RGB(52, 211, 153) - Vert foncé
      case 'Faux numéro':
        return '#FECACA'; // RGB(254, 202, 202) - Rouge clair
      case 'Mauvais numéro':
        return '#FECACA'; // RGB(254, 202, 202) - Rouge clair  
      case 'Hors cible':
        return '#FCA5A5'; // RGB(252, 165, 165) - Rouge moyen
      case 'Pas intéressé':
        return '#F87171'; // RGB(248, 113, 113) - Rouge vif
      case 'Annulé':
        return '#EF4444'; // RGB(239, 68, 68) - Rouge foncé
      case 'Injoignable':
        return '#D1D5DB'; // RGB(209, 213, 219) - Gris moyen
      default:
        return '#E5E7EB'; // RGB(229, 231, 235) - Gris par défaut
    }
  };

    return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 sm:gap-0">
        <div></div>
        {/* Boutons d'action - visible uniquement sur l'onglet Opportunité */}
        {activeTab === 'opportunite' && (
          <div className="flex items-center gap-3 sm:gap-4 w-full sm:w-auto">
            {/* Bouton Parrainage */}
            <button 
              onClick={handleParrainageClick}
              className="flex items-center gap-2 px-4 py-2 bg-cyan-500 hover:bg-cyan-600 text-white rounded-lg transition-colors duration-200"
            >
              <Users className="w-4 h-4" />
              <span className="font-medium">Parrainage</span>
            </button>
          </div>
        )}
      </div>

      {/* Carte prospects assignés au-dessus des onglets */}
      <div className="w-full">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 w-full max-w-md">
          <div className="flex items-center gap-3">
            <UserPlus className="w-8 h-8 text-blue-600" />
            <div className="flex flex-col">
              <span className="font-semibold text-gray-900 text-lg">
                {todayProspectsCount} prospects assignés
              </span>
              <span className="text-sm text-gray-600">
                aujourd'hui
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Onglets principaux */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="flex flex-wrap sm:flex-nowrap space-x-4 sm:space-x-8 px-4 sm:px-6 overflow-x-auto" aria-label="Tabs">
            <button
              onClick={() => {
                setActiveTab('opportunite');
                setActiveSubTab('nouveau-prospect');
              }}
              className={`flex-shrink-0 py-4 px-2 sm:px-1 border-b-2 font-medium text-xs sm:text-sm transition-colors ${
                activeTab === 'opportunite'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-1 sm:gap-2">
                <Target className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                <span className="whitespace-nowrap">Opportunité</span>
                <span className="bg-green-100 text-green-600 py-1 px-1.5 sm:px-2 rounded-full text-xs flex-shrink-0">
                  {opportuniteCount}
                </span>
              </div>
            </button>
            <button
              onClick={() => {
                setActiveTab('perdu');
                setActiveSubTabPerdu('faux-numero');
              }}
              className={`flex-shrink-0 py-4 px-2 sm:px-1 border-b-2 font-medium text-xs sm:text-sm transition-colors ${
                activeTab === 'perdu'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-1 sm:gap-2">
                <XCircle className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                <span className="whitespace-nowrap">Perdu</span>
                <span className="bg-red-100 text-red-600 py-1 px-1.5 sm:px-2 rounded-full text-xs flex-shrink-0">
                  {perduCount}
                </span>
              </div>
            </button>
          </nav>
        </div>

        {/* Sous-onglets pour Opportunité */}
        {activeTab === 'opportunite' && (
          <div className="bg-white border-b border-gray-200">
            <nav className="flex flex-wrap sm:flex-nowrap space-x-2 sm:space-x-4 md:space-x-8 px-4 sm:px-6 py-2 overflow-x-auto scrollbar-hide" aria-label="Sub Tabs">
              {sousOngletsOpportunite.map((sousOnglet) => {
                const Icon = sousOnglet.icon;
                const count = getSubTabCount(sousOnglet.id);
                return (
                  <button
                    key={sousOnglet.id}
                    onClick={() => setActiveSubTab(sousOnglet.id)}
                    className={`flex-shrink-0 py-3 sm:py-4 px-1 sm:px-1 border-b-2 font-medium text-xs sm:text-sm transition-colors whitespace-nowrap ${
                      activeSubTab === sousOnglet.id
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center gap-1 sm:gap-2">
                      <Icon className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                      <span className="hidden sm:inline">{sousOnglet.label}</span>
                      <span className="sm:hidden text-xs">{sousOnglet.label.split(' ')[0]}</span>
                      {count > 0 && (
                        <span className={`py-0.5 sm:py-1 px-1 sm:px-2 rounded-full text-xs flex-shrink-0 ${
                          activeSubTab === sousOnglet.id
                            ? 'bg-green-100 text-green-600'
                            : 'bg-gray-100 text-gray-600'
                        }`}>
                          {count}
                        </span>
                      )}
                    </div>
                  </button>
                );
              })}
            </nav>
          </div>
        )}


        {/* Sous-onglets NRP */}
        {activeTab === 'opportunite' && activeSubTab === 'ne-repond-pas' && (
          <div className="bg-gray-50 border-b border-gray-200">
            <nav className="flex flex-wrap sm:flex-nowrap space-x-2 sm:space-x-4 md:space-x-8 px-4 sm:px-6 py-2 overflow-x-auto scrollbar-hide" aria-label="Sub Tabs NRP">
              {[
                { id: 'ne-repond-pas', label: 'Ne r\u00e9pond pas' },
                { id: 'ne-repond-pas-2', label: 'Ne r\u00e9pond pas 2' },
                { id: 'ne-repond-pas-3', label: 'Ne r\u00e9pond pas 3' },
                { id: 'ne-repond-pas-3plus', label: 'Ne r\u00e9pond pas 3+' },
              ].map((tab) => {
                const count = prospects.filter(p => p.statut === tab.label && p.category === 'opportunite').length;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveNRPTab(tab.id)}
                    className={`flex-shrink-0 py-2 sm:py-3 px-1 border-b-2 font-medium text-xs sm:text-sm transition-colors whitespace-nowrap ${
                      activeNRPTab === tab.id
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center gap-1 sm:gap-2">
                      <PhoneOff className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                      <span>{tab.label}</span>
                      {count > 0 && (
                        <span className={`py-0.5 px-1 sm:px-2 rounded-full text-xs flex-shrink-0 ${
                          activeNRPTab === tab.id
                            ? 'bg-blue-100 text-blue-600'
                            : 'bg-gray-100 text-gray-600'
                        }`}>
                          {count}
                        </span>
                      )}
                    </div>
                  </button>
                );
              })}
            </nav>
          </div>
        )}
        {/* Sous-onglets pour Perdu */}
        {activeTab === 'perdu' && (
          <div className="bg-white border-b border-gray-200">
            <nav className="flex flex-wrap sm:flex-nowrap space-x-2 sm:space-x-4 md:space-x-8 px-4 sm:px-6 py-2 overflow-x-auto scrollbar-hide" aria-label="Sub Tabs Perdu">
              {sousOngletsPerdu.map((sousOnglet) => {
                const Icon = sousOnglet.icon;
                const count = getSubTabCountPerdu(sousOnglet.id);
                return (
                  <button
                    key={sousOnglet.id}
                    onClick={() => setActiveSubTabPerdu(sousOnglet.id)}
                    className={`flex-shrink-0 py-3 sm:py-4 px-1 sm:px-1 border-b-2 font-medium text-xs sm:text-sm transition-colors whitespace-nowrap ${
                      activeSubTabPerdu === sousOnglet.id
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center gap-1 sm:gap-2">
                      <Icon className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                      <span className="hidden sm:inline">{sousOnglet.label}</span>
                      <span className="sm:hidden text-xs">{sousOnglet.label.split(' ')[0]}</span>
                      {count > 0 && (
                        <span className={`py-0.5 sm:py-1 px-1 sm:px-2 rounded-full text-xs flex-shrink-0 ${
                          activeSubTabPerdu === sousOnglet.id
                            ? 'bg-red-100 text-red-600'
                            : 'bg-gray-100 text-gray-600'
                        }`}>
                          {count}
                        </span>
                      )}
                    </div>
                  </button>
                );
              })}
            </nav>
          </div>
        )}
      </div>

      {/* Contenu de l'onglet actif */}
      <div className="space-y-6">


        {/* Tableau des prospects pour la catégorie active */}
        {filteredProspects.length > 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="bg-gray-50 p-4 rounded-lg space-y-3">
              {/* En-têtes des colonnes - Différent pour Admin et Commercial */}
              {isAdmin ? (
                <div className="bg-gray-100 rounded-lg p-3 flex items-center gap-6 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="w-1"></div> {/* Espace pour le liseré */}
                  <div className="flex-[2.5] pl-3">Prospect</div>
                  <div className="hidden sm:flex flex-[2] justify-center">Contact</div>
                  <div className="flex-[1] text-center">Statut</div>
                  <div className="hidden lg:flex flex-[1.5] justify-center">Commercial</div>
                  <div className="hidden lg:flex flex-[1.2] justify-center">Reçu le</div>
                  <div className="flex-[1] text-center">Actions</div>
                </div>
              ) : (
                <div className="bg-gray-100 rounded-lg p-3 flex items-center gap-6 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="w-1"></div> {/* Espace pour le liseré */}
                  <div className="flex-[2.5] pl-3">Prospect</div>
                  <div className="hidden sm:flex flex-[2] justify-center">Contact</div>
                  <div className="flex-[1.2] text-center">Statut</div>
                  <div className="hidden lg:flex flex-[1.2] justify-center">Assigné le</div>
                  <div className="flex-[1] text-center">Actions</div>
                </div>
              )}
              
              <div className="space-y-3">
                {filteredProspects.map((prospect, index) => (
                    <div
                      key={prospect.id}
                      className="bg-white hover:bg-gray-50 transition-all duration-200 cursor-pointer rounded-lg shadow-sm border border-gray-200 hover:shadow-md p-4 flex items-center gap-6"
                      onClick={(e) => handleProspectClick(e, prospect)}
                      title="Cliquer pour ouvrir la fiche de rendez-vous"
                    >
                      {/* Liseré fin coloré du statut */}
                      <div 
                        className="w-1 h-16 flex-shrink-0"
                        style={{ 
                          backgroundColor: getStatusColor(prospect.statut),
                          borderRadius: '6px'
                        }}
                      ></div>
                      
                      {/* Informations prospect */}
                      <div className="flex-[2.5] pl-3 min-w-0">
                        <div>
                          <div className="text-sm font-medium text-gray-900 break-words flex items-center gap-2">
                            <span>{prospect.prenom} {prospect.nom}</span>
                            {hasRecentNotes(prospect) && (
                              <span className="text-xs" title="Notes récentes (dernières 24h)">📝</span>
                            )}
                          </div>
                          <div className="text-sm font-medium text-gray-900 flex items-center gap-1 mt-1">
                            <MapPin className="w-3 h-3 flex-shrink-0" />
                            <span className="break-words">
                              {prospect.adresse && <span>{prospect.adresse}</span>}
                              {prospect.adresse && prospect.ville && ', '}
                              {prospect.ville && <span>{prospect.ville}</span>}
                              {prospect.ville && prospect.code_postal && ', '}
                              {prospect.code_postal && <span>{prospect.code_postal}</span>}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Contact */}
                      <div className="hidden sm:flex flex-[2] justify-center min-w-0">
                        <div className="space-y-1 text-center w-full">
                          <div className="flex items-center justify-center gap-2 text-sm text-gray-600">
                            <Phone className="w-3 h-3 flex-shrink-0" />
                            <a href={`tel:${prospect.numero_telephone || prospect.telephone}`} className="hover:text-primary break-words">
                              {formatPhoneNumber(prospect.numero_telephone || prospect.telephone)}
                            </a>
                          </div>
                          <div className="flex items-center justify-center gap-2 text-sm text-gray-600">
                            <Mail className="w-3 h-3 flex-shrink-0" />
                            <span className="break-all text-xs" title={prospect.mail || prospect.email}>
                              {prospect.mail || prospect.email}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Affichage différent pour Admin et Commercial */}
                      {isAdmin ? (
                        <>
                          {/* ADMIN - Statut : Assigné / Non assigné */}
                          <div className="flex-[1] flex justify-center min-w-0">
                            <span className={`inline-flex items-center gap-1 px-3 py-1 text-xs font-medium rounded-full ${
                              prospect.commercial_assigne_id 
                                ? 'bg-green-100 text-green-800 border border-green-200' 
                                : 'bg-gray-100 text-gray-800 border border-gray-200'
                            }`}>
                              {prospect.commercial_assigne_id ? (
                                <>
                                  <CheckCircle className="w-3 h-3" />
                                  <span>Assigné</span>
                                </>
                              ) : (
                                <>
                                  <AlertCircle className="w-3 h-3" />
                                  <span>Non assigné</span>
                                </>
                              )}
                            </span>
                          </div>

                          {/* ADMIN - Commercial */}
                          <div className="hidden lg:flex flex-[1.5] justify-center min-w-0">
                            <div className="text-center">
                              {prospect.commercial_nom ? (
                                <span className="text-sm font-medium text-gray-900">
                                  {prospect.commercial_nom}
                                </span>
                              ) : (
                                <span className="text-sm text-gray-400 italic">
                                  Non assigné
                                </span>
                              )}
                            </div>
                          </div>

                          {/* ADMIN - Date de réception */}
                          <div className="hidden lg:flex flex-[1.2] justify-center min-w-0">
                            <div className="text-center">
                              <div className="text-sm text-gray-900">
                                {new Date(prospect.dateArrivee).toLocaleDateString('fr-FR')}
                              </div>
                              <div className="text-xs text-gray-500">
                                {new Date(prospect.dateArrivee).toLocaleTimeString('fr-FR', {
                                  hour: '2-digit',
                                  minute: '2-digit',
                                  timeZone: 'Europe/Paris'
                                })}
                              </div>
                            </div>
                          </div>
                        </>
                      ) : (
                        <>
                          {/* COMMERCIAL - Statut détaillé cliquable */}
                          <div className="flex-[1.2] flex justify-center min-w-0">
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleStatusClick(e, prospect);
                              }}
                              className="inline-flex items-center gap-1 px-3 py-1 text-xs font-medium rounded-full transition-all hover:scale-105 hover:shadow-md cursor-pointer border border-gray-200 max-w-full"
                              style={{ 
                                backgroundColor: getStatusColor(prospect.statut),
                                color: '#374151'
                              }}
                              title="Cliquer pour changer le statut"
                            >
                              {getStatutIcon(prospect.statut)}
                              <span className="hidden sm:inline truncate">{prospect.statut}</span>
                              <span className="sm:hidden">{prospect.statut.split(' ')[0]}</span>
                              <ChevronDown className="w-3 h-3 ml-1 flex-shrink-0" />
                            </button>
                          </div>

                          {/* COMMERCIAL - Date d'assignation */}
                          <div className="hidden lg:flex flex-[1.2] justify-center min-w-0">
                            <div className="text-center">
                              <div className="text-sm text-gray-900">
                                {new Date(prospect.dateArrivee).toLocaleDateString('fr-FR')}
                              </div>
                              <div className="text-xs text-gray-500">
                                {new Date(prospect.dateArrivee).toLocaleTimeString('fr-FR', {
                                  hour: '2-digit',
                                  minute: '2-digit',
                                  timeZone: 'Europe/Paris'
                                })}
                              </div>
                            </div>
                          </div>
                        </>
                      )}

                      {/* Actions */}
                      <div className="flex-[1] flex justify-center min-w-0">
                        <div className="flex items-center gap-2 justify-center">
                          <button 
                            onClick={(e) => e.stopPropagation()}
                            className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors" 
                            title="Appeler"
                          >
                            <Phone className="w-4 h-4" />
                          </button>
                          <button 
                            onClick={(e) => e.stopPropagation()}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" 
                            title="Envoyer email"
                          >
                            <Mail className="w-4 h-4" />
                          </button>
                          <button 
                            onClick={(e) => handleNoteClick(e, prospect)}
                            className="p-2 text-gray-600 hover:bg-gray-50 rounded-lg transition-colors" 
                            title="Ajouter note"
                          >
                            <Plus className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
            {activeTab === 'opportunite' ? (
              <>
                {(() => {
                  const sousOnglet = sousOngletsOpportunite.find(so => so.id === activeSubTab);
                  const Icon = sousOnglet?.icon || Target;
                  return <Icon className="w-16 h-16 text-gray-400 mx-auto mb-4" />;
                })()}
                <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun prospect trouvé</h3>
                <p className="text-gray-600">
                  Aucun prospect avec le statut "{sousOngletsOpportunite.find(so => so.id === activeSubTab)?.label}"
                </p>
              </>
            ) : (
              <>
                {(() => {
                  const sousOnglet = sousOngletsPerdu.find(so => so.id === activeSubTabPerdu);
                  const Icon = sousOnglet?.icon || XCircle;
                  return <Icon className="w-16 h-16 text-gray-400 mx-auto mb-4" />;
                })()}
                <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun prospect trouvé</h3>
                <p className="text-gray-600">
                  Aucun prospect avec le statut "{sousOngletsPerdu.find(so => so.id === activeSubTabPerdu)?.label}"
                </p>
              </>
            )}
          </div>
        )}
      </div>



      {/* Modale de changement de statut - NOUVEAU DESIGN v8 (3 sections + sans commentaire) */}
      {statusDropdownOpen && selectedStatusProspect && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={(e) => {
            // Fermeture au clic en dehors de la modale
            if (e.target === e.currentTarget) {
              setStatusDropdownOpen(false);
              setSelectedStatusProspect(null);
              setStatusCommentaire('');
            }
          }}
        >
          <div 
            ref={dropdownRef} 
            className="bg-white rounded-xl shadow-xl p-6 max-w-md w-full"
            style={{ maxHeight: '600px', display: 'flex', flexDirection: 'column' }}
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex-shrink-0">
              Changer le statut de {selectedStatusProspect.prenom} {selectedStatusProspect.nom}
            </h3>

            {/* Container scrollable interne */}
            <div className="overflow-y-auto" style={{ flex: 1, paddingRight: '8px', marginRight: '-8px' }}>

              {/* SECTION 1 : Opportunités (vert) - statuts positifs sans NRP */}
              <div className="mb-4">
                <h4 className="text-sm font-medium text-green-700 mb-2 flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" />
                  Opportunités
                </h4>
                <div className="space-y-1">
                  {allStatuses
                    .filter(s => s.category === 'opportunite' && !s.label.startsWith('Ne répond pas'))
                    .map((status) => {
                      const Icon = status.icon;
                      const isCurrentStatus = selectedStatusProspect.statut === status.label;
                      const isAvailable = isStatusAvailable(status, selectedStatusProspect);
                      const isDisabled = isCurrentStatus || !isAvailable;
                      
                      return (
                        <button
                          key={`opportunite-${status.label}`}
                          onClick={() => !isDisabled && handleStatusChange(status.label, 'opportunite')}
                          disabled={isDisabled}
                          className={`w-full text-left px-3 py-2 rounded-lg border transition-colors flex items-center gap-2 relative overflow-hidden ${
                            isDisabled
                              ? 'bg-gray-100 text-gray-400 cursor-not-allowed opacity-50'
                              : 'hover:bg-green-50 hover:border-green-200 cursor-pointer border-transparent'
                          }`}
                        >
                          <div 
                            className="absolute left-0 top-0 bottom-0"
                            style={{ width: '3px', backgroundColor: getStatusColor(status.label) }}
                          ></div>
                          <Icon className="w-4 h-4 ml-1" />
                          <span className="text-sm">{status.label}</span>
                          {selectedStatusProspect.statut === status.label && (
                            <span className="ml-auto text-xs text-gray-500">(Actuel)</span>
                          )}
                        </button>
                      );
                    })}
                </div>
              </div>

              {/* SECTION 2 : Suivi à relancer (orange) - tous les "Ne répond pas" + À rappeler */}
              <div className="mb-4">
                <h4 className="text-sm font-medium mb-2 flex items-center gap-2" style={{ color: '#c2410c' }}>
                  <Clock className="w-4 h-4" />
                  Suivi à relancer
                </h4>
                <div className="space-y-1">
                  {allStatuses
                    .filter(s => s.category === 'opportunite' && s.label.startsWith('Ne répond pas'))
                    .map((status) => {
                      const Icon = status.icon;
                      const isCurrentStatus = selectedStatusProspect.statut === status.label;
                      const isAvailable = isStatusAvailable(status, selectedStatusProspect);
                      const isDisabled = isCurrentStatus || !isAvailable;
                      
                      return (
                        <button
                          key={`suivi-${status.label}`}
                          onClick={() => !isDisabled && handleStatusChange(status.label, 'opportunite')}
                          disabled={isDisabled}
                          className={`w-full text-left px-3 py-2 rounded-lg border transition-colors flex items-center gap-2 relative overflow-hidden ${
                            isDisabled
                              ? 'bg-gray-100 text-gray-400 cursor-not-allowed opacity-50'
                              : 'hover:bg-orange-50 hover:border-orange-200 cursor-pointer border-transparent'
                          }`}
                        >
                          <div 
                            className="absolute left-0 top-0 bottom-0"
                            style={{ width: '3px', backgroundColor: getStatusColor(status.label) }}
                          ></div>
                          <Icon className="w-4 h-4 ml-1" />
                          <span className="text-sm">{status.label}</span>
                          {selectedStatusProspect.statut === status.label && (
                            <span className="ml-auto text-xs text-gray-500">(Actuel)</span>
                          )}
                        </button>
                      );
                    })}
                </div>
              </div>

              {/* SECTION 3 : Prospects perdus (rouge) */}
              <div>
                <h4 className="text-sm font-medium text-red-700 mb-2 flex items-center gap-2">
                  <XCircle className="w-4 h-4" />
                  Prospects perdus
                </h4>
                <div className="space-y-1">
                  {allStatuses.filter(s => s.category === 'perdu').map((status) => {
                    const Icon = status.icon;
                    const isCurrentStatus = selectedStatusProspect.statut === status.label;
                    const isAvailable = isStatusAvailable(status, selectedStatusProspect);
                    const isDisabled = isCurrentStatus || !isAvailable;
                    
                    return (
                      <button
                        key={`perdu-${status.label}`}
                        onClick={() => !isDisabled && handleStatusChange(status.label, 'perdu')}
                        disabled={isDisabled}
                        className={`w-full text-left px-3 py-2 rounded-lg border transition-colors flex items-center gap-2 relative overflow-hidden ${
                          isDisabled
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed opacity-50'
                            : 'hover:bg-red-50 hover:border-red-200 cursor-pointer border-transparent'
                        }`}
                      >
                        <div 
                          className="absolute left-0 top-0 bottom-0"
                          style={{ width: '3px', backgroundColor: getStatusColor(status.label) }}
                        ></div>
                        <Icon className="w-4 h-4 ml-1" />
                        <span className="text-sm">{status.label}</span>
                        {selectedStatusProspect.statut === status.label && (
                          <span className="ml-auto text-xs text-gray-500">(Actuel)</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

            </div>

            <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
              <button
                onClick={() => {
                  setStatusDropdownOpen(false);
                  setSelectedStatusProspect(null);
                  setStatusCommentaire('');
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de parrainage */}
      {parrainageModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              {/* En-tête du modal */}
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                  <Users className="w-6 h-6 text-cyan-500" />
                  Ajouter un prospect par parrainage
                </h2>
                <button
                  onClick={() => setParrainageModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>

              {/* Formulaire */}
              <div className="space-y-4">
                {/* Nom et Prénom */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Prénom *
                    </label>
                    <input
                      type="text"
                      value={newProspectData.prenom}
                      onChange={(e) => setNewProspectData({...newProspectData, prenom: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                      placeholder="Prénom du prospect"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nom *
                    </label>
                    <input
                      type="text"
                      value={newProspectData.nom}
                      onChange={(e) => setNewProspectData({...newProspectData, nom: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                      placeholder="Nom du prospect"
                    />
                  </div>
                </div>

                {/* Téléphone et Email */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Téléphone *
                    </label>
                    <input
                      type="tel"
                      value={newProspectData.numero_telephone}
                      onChange={(e) => setNewProspectData({...newProspectData, numero_telephone: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                      placeholder="06 XX XX XX XX"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email
                    </label>
                    <input
                      type="email"
                      value={newProspectData.mail}
                      onChange={(e) => setNewProspectData({...newProspectData, mail: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                      placeholder="email@exemple.fr"
                    />
                  </div>
                </div>

                {/* Adresse */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Adresse
                  </label>
                  <input
                    type="text"
                    value={newProspectData.adresse}
                    onChange={(e) => setNewProspectData({...newProspectData, adresse: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                    placeholder="Adresse complète"
                  />
                </div>

                {/* Ville et Code postal */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Ville
                    </label>
                    <input
                      type="text"
                      value={newProspectData.ville}
                      onChange={(e) => setNewProspectData({...newProspectData, ville: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                      placeholder="Ville"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Code postal
                    </label>
                    <input
                      type="text"
                      value={newProspectData.code_postal}
                      onChange={(e) => setNewProspectData({...newProspectData, code_postal: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                      placeholder="75000"
                    />
                  </div>
                </div>

                {/* Client parrain avec recherche intelligente */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Client parrain (optionnel)
                  </label>
                  <ClientSearchBar
                    selectedClient={selectedClientParrain}
                    onClientSelect={setSelectedClientParrain}
                    placeholder="Rechercher le client qui recommande ce prospect..."
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Tapez le nom, entreprise, téléphone ou adresse du client qui recommande ce prospect
                  </p>
                </div>

                {/* Statut initial */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Statut initial
                  </label>
                  <select
                    value={newProspectData.statut}
                    onChange={(e) => {
                      const selectedStatus = e.target.value;
                      const category = allStatuses.find(s => s.label === selectedStatus)?.category || 'opportunite';
                      setNewProspectData({
                        ...newProspectData, 
                        statut: selectedStatus,
                        category: category
                      });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                  >
                    {allStatuses.map((status) => (
                      <option key={status.label} value={status.label}>
                        {status.label} ({status.category === 'opportunite' ? 'Opportunité' : 'Perdu'})
                      </option>
                    ))}
                  </select>
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Notes / Commentaires
                  </label>
                  <textarea
                    value={newProspectData.notes}
                    onChange={(e) => setNewProspectData({...newProspectData, notes: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                    rows="3"
                    placeholder="Informations complémentaires sur le prospect..."
                  />
                </div>
              </div>

              {/* Boutons d'action */}
              <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200">
                <button
                  onClick={() => setParrainageModalOpen(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={handleSaveNewProspect}
                  className="px-6 py-2 bg-cyan-500 hover:bg-cyan-600 text-white rounded-lg transition-colors font-medium"
                >
                  Créer le prospect
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal pour ajouter une note rapide */}
      {noteModalOpen && selectedNoteProspect && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Ajouter une note - {selectedNoteProspect.prenom} {selectedNoteProspect.nom}
            </h3>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Votre note
              </label>
              <textarea
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                placeholder="Ex: Prospect intéressé, rappeler demain à 14h..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent resize-none"
                rows={4}
                autoFocus
              />
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setNoteModalOpen(false);
                  setNoteText('');
                  setSelectedNoteProspect(null);
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handleSaveNote}
                className="px-4 py-2 bg-cyan-500 hover:bg-cyan-600 text-white rounded-lg transition-colors"
              >
                Sauvegarder
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};
  

export default ProspectsEntrants;